<section class="hd-role-gates-cheat-sheet-page">
  <!-- Controls -->
  <e-card2 class="hd-role-gates-cheat-sheet-page__controls-card" padding="2">
    <div class="hd-role-gates-cheat-sheet-page__controls" aria-label="Role gates cheat sheet controls">
      <e-hd-content-language-picker></e-hd-content-language-picker>

      <span class="hd-role-gates-cheat-sheet-page__divider"></span>

      <button type="button" class="hd-role-gates-cheat-sheet-page__control" (click)="print()" aria-label="Print de cheat sheet">
        <i eIcon icon="print"></i>
        <span>Print</span>
      </button>
    </div>
  </e-card2>

  @if (pageTitle()) {
    <h2 class="hd-role-gates-cheat-sheet-page__title">{{ pageTitle() }}</h2>
  }

  <div class="hd-role-gates-cheat-sheet-page__deck">
    <!-- Rij 1: de drie rollen-panelen -->
    <div class="hd-role-gates-cheat-sheet-page__panels">
      @for (panel of panels(); track panel.key) {
        <article class="role-panel" [style.--panel-accent]="panel.accent">
          <h3 class="role-panel__title">{{ panel.title }}</h3>
          <div class="role-panel__diagram">
            <img class="role-panel__image" [src]="panel.image" [alt]="panel.title" />
            @for (note of panel.notes; track $index) {
              <p class="role-panel__note">{{ note }}</p>
            }
          </div>
          <div class="role-panel__columns">
            <div class="role-panel__column">
              <p class="role-panel__caption">{{ panel.extCaption }}</p>
              <h4 class="role-panel__heading">{{ panel.extHeading }}</h4>
              <ul class="role-panel__roles">
                @for (role of panel.extRoles; track role.line) {
                  <li class="role-panel__role">
                    <span class="role-panel__role-name">{{ role.line }} - {{ role.name }}</span>
                    @if (role.alt) {
                      <span class="role-panel__role-alt">({{ role.line }} - {{ role.alt }})</span>
                    }
                  </li>
                }
              </ul>
            </div>
            <div class="role-panel__column">
              <p class="role-panel__caption">{{ panel.intCaption }}</p>
              <h4 class="role-panel__heading">{{ panel.intHeading }}</h4>
              <ul class="role-panel__roles">
                @for (role of panel.intRoles; track role.line) {
                  <li class="role-panel__role">
                    <span class="role-panel__role-name">{{ role.line }} - {{ role.name }}</span>
                    @if (role.alt) {
                      <span class="role-panel__role-alt">({{ role.line }} - {{ role.alt }})</span>
                    }
                  </li>
                }
              </ul>
            </div>
          </div>
          <p class="role-panel__summary">{{ panel.summary }}</p>
        </article>
      }
    </div>

    <!-- Rij 2: 8 rol- & profielpoorten -->
    <article class="profiling-panel">
      <h3 class="profiling-panel__title">{{ profilingTitle() }}</h3>
      <div class="profiling-panel__columns">
        <div class="profiling-panel__column">
          <p class="profiling-panel__heading">{{ profilingPersonalityHeading() }}</p>
          <p class="profiling-panel__note">{{ profilingPersonalityNote() }}</p>
          @for (item of profilingPersonality(); track item.gate) {
            <div class="profiling-item">
              <span class="profiling-item__gate">{{ item.gate }}</span>
              <div class="profiling-item__text">
                <p class="profiling-item__name">
                  {{ item.name }}
                  @if (item.alt) {
                    <span class="profiling-item__alt">({{ item.alt }})</span>
                  }
                </p>
                <p class="profiling-item__description">{{ item.description }}</p>
              </div>
            </div>
          }
        </div>
        <div class="profiling-panel__column profiling-panel__column--design">
          <p class="profiling-panel__heading">{{ profilingDesignHeading() }}</p>
          <p class="profiling-panel__note">{{ profilingDesignNote() }}</p>
          @for (item of profilingDesign(); track item.gate) {
            <div class="profiling-item">
              <span class="profiling-item__gate">{{ item.gate }}</span>
              <div class="profiling-item__text">
                <p class="profiling-item__name">
                  {{ item.name }}
                  @if (item.alt) {
                    <span class="profiling-item__alt">({{ item.alt }})</span>
                  }
                </p>
                <p class="profiling-item__description">{{ item.description }}</p>
              </div>
            </div>
          }
        </div>
      </div>
    </article>

    <!-- Rij 3: de lijnen-tabel -->
    <div class="role-table-wrap">
      <table class="role-table">
        <thead>
          <tr class="role-table__header role-table__header--roles">
            <th>{{ tableHeaders().gate }}</th>
            <th>{{ tableHeaders().roleGates }}</th>
            @for (line of [1, 2, 3, 4, 5, 6]; track line) {
              <th>{{ tableHeaders().line }} {{ line }}</th>
            }
            <th>{{ tableHeaders().typeFocus }}</th>
            <th>{{ tableHeaders().personalityDesign }}</th>
          </tr>
        </thead>
        <tbody>
          @for (row of roleTableRows(); track row.gate) {
            <tr class="role-table__row" [style.background-color]="row.rowColor">
              <td class="role-table__gate">{{ row.gate }}</td>
              <td class="role-table__label">{{ row.label }}</td>
              @for (cell of row.lines; track $index) {
                <td>{{ cell }}</td>
              }
              <td>
                <b>{{ row.typeMain }}</b>
                <span class="role-table__accent" [style.color]="row.typeAccentColor">/ {{ row.typeAccent }}</span>
              </td>
              <td>
                <b>{{ row.personality }}</b>
                <span class="role-table__design">/ {{ row.design }}</span>
              </td>
            </tr>
          }
        </tbody>
        <tbody>
          <tr class="role-table__header role-table__header--profiling">
            <th>{{ tableHeaders().gate }}</th>
            <th>{{ tableHeaders().profilingGates }}</th>
            @for (line of [1, 2, 3, 4, 5, 6]; track line) {
              <th>{{ tableHeaders().line }} {{ line }}</th>
            }
            <th>{{ tableHeaders().type }}</th>
            <th>{{ tableHeaders().focus }}</th>
          </tr>
          @for (row of profilingTableRows(); track row.gate) {
            <tr class="role-table__row" [style.background-color]="row.rowColor">
              <td class="role-table__gate">{{ row.gate }}</td>
              <td class="role-table__label">{{ row.label }}</td>
              @for (cell of row.lines; track $index) {
                <td>{{ cell }}</td>
              }
              <td>
                <b>{{ row.typeMain }}</b>
                <span class="role-table__accent" [style.color]="row.typeAccentColor">/ {{ row.typeAccent }}</span>
              </td>
              <td>
                <b>{{ row.personality }}</b>
                <span class="role-table__design">/ {{ row.design }}</span>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
</section>
